import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const TELEGRAM_LOGIN_SCRIPT = 'https://telegram.org/js/telegram-login.js';

// Configure axios defaults
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }
});

function loadTelegramLoginScript() {
    if (window.Telegram?.Login?.auth) {
        return Promise.resolve();
    }
    const existing = document.querySelector(`script[src="${TELEGRAM_LOGIN_SCRIPT}"]`);
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error('Failed to load Telegram login')), { once: true });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = TELEGRAM_LOGIN_SCRIPT;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Telegram login'));
        document.head.appendChild(script);
    });
}

const Login = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromParam = searchParams.get('from');
    const fromWatch = fromParam === 'watch';
    const fromFo = fromParam === 'foweb' || fromParam === 'fo2';
    const [watchNotice, setWatchNotice] = useState('');
    const [foNotice, setFoNotice] = useState('');
    const [loading, setLoading] = useState(false);

    const postLoginPath = useMemo(() => {
        if (fromWatch) return '/watch';
        if (fromFo) return '/foweb';
        return '/main-dashboard';
    }, [fromWatch, fromFo]);

    useEffect(() => {
        if (!fromWatch) return;
        const notice = sessionStorage.getItem('watch_auth_notice');
        if (notice === 'session_expired') {
            setWatchNotice(
                'Watch admin could not verify your session (watch API may be down). Log in again, then open WATCH after watchapi is healthy.'
            );
            sessionStorage.removeItem('watch_auth_notice');
        }
    }, [fromWatch]);

    useEffect(() => {
        if (!fromFo) return;
        const notice =
            sessionStorage.getItem('foweb_auth_notice') ||
            sessionStorage.getItem('fo2_auth_notice');
        if (notice === 'session_expired') {
            setFoNotice(
                'FO admin could not verify your session (FO API may be down). Log in again, then open FO after foapi is healthy.'
            );
            sessionStorage.removeItem('foweb_auth_notice');
            sessionStorage.removeItem('fo2_auth_notice');
        }
    }, [fromFo]);

    useEffect(() => {
        loadTelegramLoginScript().catch((error) => {
            console.error(error);
        });
    }, []);

    const completeLogin = async (idToken) => {
        const response = await api.post('api/auth/telegram', {
            id_token: idToken,
            from: fromWatch ? 'watch' : fromFo ? 'foweb' : 'erp',
        });

        if (!response.data.isAdmin) {
            alert('You do not have admin rights. Please contact the administrator.');
            return;
        }

        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        setTimeout(() => {
            if (postLoginPath === '/watch' || postLoginPath === '/foweb') {
                window.location.href = postLoginPath;
            } else {
                navigate(postLoginPath);
            }
        }, 100);
    };

    const handleTelegramLogin = async () => {
        if (loading) return;
        setLoading(true);
        try {
            await loadTelegramLoginScript();
            if (!window.Telegram?.Login?.auth) {
                throw new Error('Telegram login is not available');
            }

            const { data } = await api.get('api/auth/telegram/oidc');
            const clientId = Number(data.clientId);
            if (!Number.isFinite(clientId) || !data.nonce) {
                throw new Error('Telegram OIDC is not configured');
            }

            window.Telegram.Login.auth(
                {
                    client_id: clientId,
                    scope: ['profile'],
                    nonce: data.nonce,
                },
                async (result) => {
                    try {
                        if (!result?.id_token) {
                            if (result?.error && result.error !== 'popup_closed') {
                                alert(result.error);
                            }
                            return;
                        }
                        setLoading(true);
                        await completeLogin(result.id_token);
                    } catch (error) {
                        console.error('Login error details:', {
                            message: error.message,
                            response: error.response?.data,
                            status: error.response?.status
                        });
                        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Login failed. Please try again.';
                        alert(errorMessage);
                    } finally {
                        setLoading(false);
                    }
                }
            );
            setLoading(false);
        } catch (error) {
            console.error('Login error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Login failed. Please try again.';
            alert(errorMessage);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-2xl w-96 border border-white/20">
                <div className="text-center space-y-6">
                    {watchNotice && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 text-left">
                            {watchNotice}
                        </p>
                    )}
                    {foNotice && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 text-left">
                            {foNotice}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={handleTelegramLogin}
                        disabled={loading}
                        className="w-full rounded-lg bg-[#54a9eb] px-4 py-3 text-white font-medium shadow-md hover:bg-[#4b9ad6] disabled:opacity-60"
                    >
                        {loading ? 'Signing in…' : 'Log in with Telegram'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login;
